import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { WallPost } from '../components/WallPost'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import SkeletonLoader from '../components/SkeletonLoader'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

export default function ActivityPage() {
  const { user } = useAuth()
  const [posts, setPosts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [likedPosts, setLikedPosts] = useState(new Set())
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [postError, setPostError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get('/api/wall/feed?limit=20&offset=0', { headers })
      setPosts(data.posts)
      setTotal(data.total)
      setLikedPosts(new Set((data.posts ?? []).filter(p => p.likedByMe).map(p => p.id)))
    } catch { /* empty */ }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function handlePost(e) {
    e.preventDefault()
    const content = draft.trim()
    if (!content) return
    setPosting(true)
    setPostError(null)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post('/api/wall/posts', { content }, { headers })
      setPosts(prev => [data, ...prev])
      setTotal(prev => prev + 1)
      setDraft('')
    } catch (err) {
      setPostError(err.response?.data?.error ?? 'Failed to post.')
    } finally { setPosting(false) }
  }

  async function handleLoadMore() {
    setLoadingMore(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get(`/api/wall/feed?limit=20&offset=${posts.length}`, { headers })
      setPosts(prev => [...prev, ...data.posts])
      setTotal(data.total)
      setLikedPosts(prev => { const s = new Set(prev); (data.posts ?? []).filter(p => p.likedByMe).forEach(p => s.add(p.id)); return s })
    } catch { /* empty */ }
    setLoadingMore(false)
  }

  async function handleDeletePost(postId) {
    if (!window.confirm('Delete this post?')) return
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/wall/posts/${postId}`, { headers })
      setPosts(prev => prev.filter(p => p.id !== postId))
      setTotal(prev => prev - 1)
    } catch { /* empty */ }
  }

  async function handleToggleLike(postId) {
    const wasLiked = likedPosts.has(postId)
    setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.delete(postId) : s.add(postId); return s })
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? -1 : 1) } : p))
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/posts/${postId}/like`, {}, { headers })
      setLikedPosts(prev => { const s = new Set(prev); data.liked ? s.add(postId) : s.delete(postId); return s })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: data.likes_count } : p))
    } catch {
      setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.add(postId) : s.delete(postId); return s })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? 1 : -1) } : p))
    }
  }

  function handleCommentsCountChange(postId, delta) {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: Math.max(0, p.comments_count + delta) } : p))
  }

  return (
    <div className="activity-page">
      <Navbar />
      <div className="activity-page__inner">
        <div className="activity-main__header">
          <h1 className="activity-main__title">Activity</h1>
          <p className="activity-main__subtitle">Wall posts from people you follow</p>
        </div>

        <form className="wall-composer" onSubmit={handlePost}>
          <textarea
            className="wall-composer__input"
            placeholder="Write something on your wall…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={5000}
            rows={3}
          />
          <div className="wall-composer__footer">
            {postError && <span className="wall-composer__error">{postError}</span>}
            <span className="wall-composer__chars">{draft.length}/5000</span>
            <button className="btn btn-primary" type="submit" disabled={posting || !draft.trim()}>
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>

        {loading ? (
          <div className="wall-list">{[...Array(3)].map((_, i) => <div key={i} className="wall-skeleton" />)}</div>
        ) : posts.length === 0 ? (
          <div className="profile-empty">
            <div className="profile-empty__icon">📝</div>
            <p className="profile-empty__title">No activity yet</p>
            <p className="profile-empty__desc">Follow some people to see their wall posts here.</p>
          </div>
        ) : (
          <>
            <div className="wall-list">
              {posts.map(post => (
                <WallPost
                  key={post.id}
                  post={post}
                  liked={likedPosts.has(post.id)}
                  onToggleLike={() => handleToggleLike(post.id)}
                  onDelete={() => handleDeletePost(post.id)}
                  onEdit={(id, updated) => setPosts(prev => prev.map(p => p.id === id ? { ...p, ...updated } : p))}
                  canDelete={user?.id === post.user_id}
                  user={user}
                  isAdmin={false}
                  onCommentsCountChange={delta => handleCommentsCountChange(post.id, delta)}
                />
              ))}
            </div>
            {posts.length < total && (
              <div className="wall-load-more">
                <button className="btn btn-outline wall-load-more__btn" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
